import { Button } from '@material-ui/core';
import SimpleModal from '../shared/SimpleModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import solicitationService from '../../domain/services/solicitation/solicitation.service';
import { requestServices } from '../../domain/constant/request.services.constant';
import utilsProvider from '../providers/utils.provider';
import { areas } from '../../domain/constant/area.constant';
import { calculationServices } from '../../domain/constant/calculation.services.constant';
import RequestCalculationServices from './calculation/request.calculation.services.component';
import announcementService from '../../domain/services/marketing/announcement.service';
import { announcements } from '../../domain/constant/announcements.constant';
import SnackbarAlert from '../shared/SnackbarAlert';

const RequestCalculationAdditionalModal = ({ open, onClose, requestAreaId, request, onFinish, onDecline }) => {
    const [calculationPrice, setCalculationPrice] = useState(null);
    const [isLoadingPrice, setIsLoadingPrice] = useState(false);
    const [selectedCalculationServiceId, setSelectedCalculationServiceId] = useState(null);

    const areasToOpenModal = [areas.trabalhista.id, areas.previdenciario.id];

    useEffect(() => {
        if (!open) return;

        if (requestAreaId === areas.trabalhista.id) {
            const defaultCalculationForLaborArea = calculationServices.settlementFundsForInitials.id;
            setSelectedCalculationServiceId(defaultCalculationForLaborArea);
        } else {
            setSelectedCalculationServiceId(null);
        }
    }, [open, requestAreaId]);

    const fetchCalculationPriceByServiceId = useCallback(
        async (calculationServiceId) => {
            if (!request?.id || !calculationServiceId) return;

            setIsLoadingPrice(true);

            const requestValidated = {
                ...request,
                forwardCalculationServiceId: calculationServiceId
            };

            try {
                const { data } = await solicitationService.getServicePrices(requestValidated);

                const calculationService = data.servicesWithPrices?.find(
                    (service) =>
                        service.serviceId === requestServices.structure.calculation.id ||
                        service.serviceDatabaseFlag ===
                            requestServices.structure.calculation.databaseKey
                );

                setCalculationPrice(calculationService?.servicePrice || null);
            } catch (error) {
                setCalculationPrice(null);
            } finally {
                setIsLoadingPrice(false);
            }
        },
        [request]
    );

    useEffect(() => {
        if (!open || !request?.id || !selectedCalculationServiceId) {
            setCalculationPrice(null);
            return;
        }

        fetchCalculationPriceByServiceId(selectedCalculationServiceId);
    }, [open, request?.id, selectedCalculationServiceId, fetchCalculationPriceByServiceId]);

    const calculationServiceProps = useMemo(() => ({
        label: "Deseja incluir o serviço de cálculo na sua solicitação?",
        request,
        requestId: request?.id,
        areaId: requestAreaId,
        calculationServiceId: selectedCalculationServiceId,
        isEdit: false,
        value: selectedCalculationServiceId,
        setFieldValue: (fieldName, value) => {
            setSelectedCalculationServiceId(value);
        },
        onSave: false,
        fieldProps: {
            titleColor: '#131313',
            titleSize: 15,
            titleWeight: 600,
            spacing: 10
        }
    }), [request, requestAreaId, selectedCalculationServiceId]);

    const getFormattedPrice = () => {
        if (isLoadingPrice) return 'Carregando...';
        if (calculationPrice) return utilsProvider.formatNumberCurrency(calculationPrice);

        return null;
    };
    
    const PriceDisplay = () => {
        if (!selectedCalculationServiceId && requestAreaId === areas.previdenciario.id) return null;

        const formattedPrice = getFormattedPrice();
        if (!formattedPrice) return null;

        return (
            <div className="mt-2 bg-gray-200/15 rounded-md p-4 border-l-4 border-gray-200/80">
                <p className="text-sm font-semibold text-foreground">
                    💲 Valor do serviço: {formattedPrice}
                </p>
            </div>
        );
    };

    const handleConfirmCalculation = async () => {
        if (requestAreaId === areas.previdenciario.id && !selectedCalculationServiceId) {
            SnackbarAlert.warning('Por favor, selecione um tipo de cálculo antes de continuar.');
            return;
        }

        try {
            await announcementService.markViewedByKey(announcements.calculationModal, {
                    requestId: request.id,
                    action: 'accepted'
                });

            onFinish?.({
                forwardCalculationServiceId: selectedCalculationServiceId,
                needCalculation: true
            });

            onClose();
        } catch (error) {
            SnackbarAlert.error('Ocorreu um erro ao adicionar o cálculo. Por favor, tente novamente.');
        }
    };

    const handleDeclineCalculation = async () => {
        await announcementService.markViewedByKey(announcements.calculationModal, {
            requestId: request.id,
            action: 'declined'
        });

        onFinish?.(null);
        onDecline?.({
            calculationSuggestionDeclinedAt: new Date().toISOString(),
        });
    };

    return (
        <>
            <SimpleModal
                title="Sua petição precisa de cálculo!"
                maxWidth="sm"
                open={open}
                onClose={onClose}
                showClose={false}
                disableBackdropClick
                disableEscapeKeyDown
            >
                <div>
                    <div className="space-y-4">
                        <p className="text-base text-foreground leading-relaxed">
                            Identificamos que sua petição exige cálculos para definir o valor exato
                            da causa — e nós podemos cuidar disso pra você.
                        </p>
                        <p className="text-base text-foreground leading-relaxed">
                            Com os cálculos prontos pela Facilita Jurídico, sua petição chega
                            completa, precisa e profissional, economizando tempo e evitando
                            retrabalho.
                        </p>
                    </div>

                    {areasToOpenModal.includes(requestAreaId) && (
                        <div className="mb-4 mt-8">
                            <RequestCalculationServices {...calculationServiceProps} />
                            <PriceDisplay />
                        </div>
                    )}

                    <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        fullWidth
                        className="!mt-3 font-bold"
                        onClick={handleConfirmCalculation}
                    >
                        Sim, quero os cálculos prontos
                    </Button>

                    <Button
                        color="primary"
                        variant="outlined"
                        size="large"
                        fullWidth
                        className="!mt-3 font-bold"
                        onClick={handleDeclineCalculation}
                    >
                        Não, continuar sem os cálculos
                    </Button>
                </div>
            </SimpleModal>
        </>
    );
};

export default RequestCalculationAdditionalModal;
